import { PANOPLY, SKILLS, SLOT_EFFECTS, UNIQUES, uniquesFor, type SkillId, type UniqueDef, type UniqueId, type UniqueSlot } from '@somnhold/content';
import { imax, imin } from '../math/fixed.ts';
import { event } from './core.ts';
import { forgeMaxPlus, preparedGear, refreshUnique, wornItems } from './items.ts';
import { syncSlot, uniquePieces } from './panoply.ts';
import { knownSkills } from './progress.ts';
import type { CampaignState, HeroState, ItemInstance, Loadout, Ms, PlayerState } from './types.ts';

/**
 * Scraps, rearming and infusing (docs/PANOPLIA.md §4, §6). Each victory in a slot's place gives scraps of that slot (the
 * weapons' in the crypts, the armours' in the incursions, the amulets' down deep); six rearm a piece of the old garrison
 * at the forge, whichever the hero picks of those whose skill it knows. Infusing burns a rare of the slot so that its
 * effect becomes the slot's, rising a rank with each more of the same.
 */

export const SCRAP_SLOTS: readonly UniqueSlot[] = ['weapon', 'armor', 'amulet'];

/** The panoply's pieces of the hero's class for a slot (the saga's piece left out: it isn't rearmed). */
export const panoplyPieces = (h: Pick<HeroState, 'heroClass'>, slot: UniqueSlot): UniqueDef[] => uniquesFor(h.heroClass, slot).filter((d) => d.rearmed);

/** Whether the hero holds a piece (worn, in the panoply or in the bag). */
export const holdsPiece = (h: HeroState, id: UniqueId): boolean => uniquePieces(h).some((x) => x.unique === id);

/** The slot's pieces the hero doesn't have yet. */
export const piecesMissing = (h: HeroState, slot: UniqueSlot): UniqueDef[] => panoplyPieces(h, slot).filter((d) => !holdsPiece(h, d.id));

/** The slot's awakening stage (the same on all its pieces). */
export const slotStage = (h: HeroState, slot: UniqueSlot): number =>
  uniquePieces(h).find((x) => x.slot === slot && !!x.unique && UNIQUES[x.unique].cls === h.heroClass)?.awaken ?? 0;

/** The most scraps of a slot kept: six for each piece still missing (a class without pieces yet: six). */
export const scrapCap = (h: HeroState, slot: UniqueSlot): number => PANOPLY.cost * imax(1, piecesMissing(h, slot).length);

/**
 * Whether scraps of a slot fall for the hero (§4): only with its saga piece come (its slot made), and no more once the
 * slot is awake to the IV with every piece of it.
 */
export const scrapsFall = (h: HeroState, slot: UniqueSlot): boolean => !!h.uniqueSlots?.[slot] && !(piecesMissing(h, slot).length === 0 && slotStage(h, slot) >= 4);

/** Gives `n` scraps of a slot (up to its cap; what goes over is lost, and the event says so). Returns how many were kept. */
export const addScraps = (s: CampaignState, p: PlayerState, slot: UniqueSlot, n: number, now: Ms): number => {
  const h = p.hero;
  if (n <= 0 || !scrapsFall(h, slot)) return 0;
  const have = h.scraps?.[slot] ?? 0;
  const cap = scrapCap(h, slot);
  const add = imax(0, imin(n, cap - have));
  if (add > 0) h.scraps = { ...h.scraps, [slot]: have + add };
  event(s, now, 'scraps', { pid: p.id, data: { slot, n: add, have: have + add, cap, ...(n > add ? { lost: n - add } : {}) } });
  return add;
};

/** The open slot with the fewest scraps (the cellars' default; docs/PANOPLIA.md §4), or null if none takes them. */
export const fewestScraps = (h: HeroState): UniqueSlot | null => {
  let best: UniqueSlot | null = null;
  for (const slot of SCRAP_SLOTS) {
    if (!scrapsFall(h, slot) || (h.scraps?.[slot] ?? 0) >= scrapCap(h, slot)) continue;
    if (best === null || (h.scraps?.[slot] ?? 0) < (h.scraps?.[best] ?? 0)) best = slot;
  }
  return best;
};

/** The slot's pieces the hero could rearm now: missing, and their skill known (a tome's, once its tome is full). */
export const rearmable = (h: HeroState, slot: UniqueSlot): UniqueDef[] =>
  piecesMissing(h, slot).filter((d) => !!d.skill && knownSkills(h, SKILLS[d.skill].slot).includes(d.skill));

/** Why a piece can't be rearmed now, or null. */
export type RearmBlock = 'notPanoply' | 'notYourClass' | 'noSlot' | 'have' | 'unknownSkill' | 'needsForge' | 'notEnoughScraps';
export const rearmBlock = (s: CampaignState, h: HeroState, id: UniqueId): RearmBlock | null => {
  const d = UNIQUES[id];
  if (!d?.rearmed) return 'notPanoply';
  if (d.cls !== h.heroClass) return 'notYourClass';
  if (!h.uniqueSlots?.[d.slot]) return 'noSlot';
  if (holdsPiece(h, id)) return 'have';
  if (!rearmable(h, d.slot).some((x) => x.id === id)) return 'unknownSkill';
  if (!forgeMaxPlus(s)) return 'needsForge';
  if ((h.scraps?.[d.slot] ?? 0) < PANOPLY.cost) return 'notEnoughScraps';
  return null;
};

/** Hilde rearms a piece: six scraps spent, the piece into the panoply with what its slot has worked. */
export const rearm = (s: CampaignState, p: PlayerState, id: UniqueId, now: Ms): RearmBlock | ItemInstance => {
  const h = p.hero;
  const block = rearmBlock(s, h, id);
  if (block) return block;
  const d = UNIQUES[id];
  h.scraps = { ...h.scraps, [d.slot]: (h.scraps?.[d.slot] ?? 0) - PANOPLY.cost };
  const item: ItemInstance = { id: s.nextId++, slot: d.slot, rarity: 'unique', attrs: { ...d.attrs }, effect: d.effect, plus: 0, unique: id, uqBase: { ...d.attrs } };
  refreshUnique(item, h.level);
  (h.panoply ??= []).push(item);
  // What the slot has worked: from any piece of it the hero already holds.
  const other = uniquePieces(h).find((x) => x !== item && x.slot === d.slot && !!x.unique && UNIQUES[x.unique].cls === h.heroClass);
  if (other) syncSlot(h, other);
  // The bastion keeps it hung (docs/PANOPLIA.md §9.6).
  const hung = (s.hung ??= []);
  if (!hung.some((x) => x.id === id)) hung.push({ id, stage: item.awaken ?? 0, item: item.id, cls: d.cls });
  event(s, now, 'rearmed', { pid: p.id, data: { unique: id, slot: d.slot, item: item.id } });
  return item;
};

/* ---------- infusing (§6) ---------- */

/** What a slot's infused effect is worth (bp of a one-handed rare's), before the awakening's steps. */
export const infusedBp = (rank: number): number => PANOPLY.infuse.baseBp + imin(rank, PANOPLY.infuse.ranks) * PANOPLY.infuse.rankBp;

/** Why a rare can't be infused into a unique now, or null. */
export type InfuseBlock = 'needsForge' | 'noItem' | 'infuseNotUnique' | 'infuseNotRare' | 'badSlot' | 'badEffect' | 'maxRank' | 'notEnough' | 'notInfused';
export const infuseBlock = (s: CampaignState, p: PlayerState, uq: ItemInstance | undefined, rare: ItemInstance | undefined): InfuseBlock | null => {
  if (!forgeMaxPlus(s)) return 'needsForge';
  if (!uq || !rare) return 'noItem';
  if (!uq.unique || UNIQUES[uq.unique].cls !== p.hero.heroClass) return 'infuseNotUnique';
  if ((rare.rarity !== 'rare' && rare.rarity !== 'veryRare') || rare.unique || !rare.effect) return 'infuseNotRare';
  if (rare.slot !== uq.slot) return 'badSlot';
  if (!SLOT_EFFECTS[uq.slot].includes(rare.effect)) return 'badEffect';
  if (uq.infused?.effect === rare.effect && uq.infused.rank >= PANOPLY.infuse.ranks) return 'maxRank';
  if (s.storage.dust < PANOPLY.infuse.dust) return 'notEnough';
  return null;
};

/** A rare burnt into the slot's effect: a new effect starts at rank 0, the same one rises a rank. The rare is lost. */
export const infuse = (s: CampaignState, p: PlayerState, uq: ItemInstance, rare: ItemInstance, now: Ms): void => {
  const h = p.hero;
  const effect = rare.effect as NonNullable<ItemInstance['effect']>;
  const rank = uq.infused?.effect === effect ? uq.infused.rank + 1 : 0;
  s.storage.dust -= PANOPLY.infuse.dust;
  // The rare goes, from the bag or from where it was worn (that slot back to its last unique, if it waits in the panoply).
  const i = h.bag.findIndex((x) => x.id === rare.id);
  if (i >= 0) h.bag.splice(i, 1);
  else
    for (const w of Object.keys(h.gear).sort() as (keyof HeroState['gear'])[]) {
      if (h.gear[w]?.id !== rare.id) continue;
      delete h.gear[w];
      const last = h.uniqueSlots?.[uq.slot as UniqueSlot]?.last;
      const j = (h.panoply ?? []).findIndex((x) => x.id === last);
      if (w === uq.slot && j >= 0) {
        h.gear[w] = h.panoply?.splice(j, 1)[0] as ItemInstance;
      }
    }
  uq.infused = { effect, rank };
  syncSlot(h, uq);
  event(s, now, 'infused', { pid: p.id, data: { unique: uq.unique ?? '', effect, rank, dust: PANOPLY.infuse.dust } });
};

/** Back to each piece's own effect: the same dust, the ranks lost. */
export const uninfuse = (s: CampaignState, p: PlayerState, uq: ItemInstance, now: Ms): InfuseBlock | null => {
  if (!forgeMaxPlus(s)) return 'needsForge';
  if (!uq.unique || UNIQUES[uq.unique].cls !== p.hero.heroClass) return 'infuseNotUnique';
  if (!uq.infused) return 'notInfused';
  if (s.storage.dust < PANOPLY.infuse.dust) return 'notEnough';
  s.storage.dust -= PANOPLY.infuse.dust;
  delete uq.infused;
  syncSlot(p.hero, uq);
  event(s, now, 'infused', { pid: p.id, data: { unique: uq.unique, effect: '', rank: 0, dust: PANOPLY.infuse.dust } });
  return null;
};

/** The pieces a hero wears or keeps that take scraps' rares: for the forge's list, the worst first. */
export const infusable = (h: HeroState, slot: UniqueSlot): ItemInstance[] =>
  [...h.bag, ...wornItems(h)].filter((x) => (x.rarity === 'rare' || x.rarity === 'veryRare') && !x.unique && !!x.effect && x.slot === slot && SLOT_EFFECTS[slot].includes(x.effect));

/* ---------- awakening with scraps (§4) ---------- */

/** Whether six scraps can pay a slot's next awakening: every piece of the slot held, and six scraps of it. */
export const scrapsPay = (h: HeroState, item: ItemInstance): boolean =>
  !!item.unique && piecesMissing(h, item.slot as UniqueSlot).length === 0 && panoplyPieces(h, item.slot as UniqueSlot).length > 0 && (h.scraps?.[item.slot as UniqueSlot] ?? 0) >= PANOPLY.cost;

/* ---------- the piece follows the skill (§2) ---------- */

/**
 * What picking `skill` on its button does to a preparation's pieces (docs/PANOPLIA.md §2): if the hero holds that
 * skill's unique and the piece the preparation would go in with in that slot is a unique of another skill of the same
 * button (left without its power), the preparation takes the skill's piece instead (`gear`, with what it was: `from`).
 * If it would go in with something else that still serves (a rare, or a piece without a skill), nothing changes and the
 * piece is only offered (`offer`). Null when the skill has no piece the hero holds, or the preparation already has it.
 */
export const followSkill = (h: HeroState, l: Pick<Loadout, 'gear'>, skill: SkillId): { gear: NonNullable<Loadout['gear']>; from: number | null; to: number } | { offer: number } | null => {
  const piece = uniquePieces(h).find((x) => !!x.unique && UNIQUES[x.unique].cls === h.heroClass && UNIQUES[x.unique].skill === skill);
  if (!piece) return null;
  const slot = piece.slot as UniqueSlot;
  const now = preparedGear(h, l.gear)[slot];
  if (now?.id === piece.id) return null;
  const other = now?.unique ? UNIQUES[now.unique] : null;
  if (other && other.cls === h.heroClass && other.skill && other.skill !== skill && SKILLS[other.skill].slot === SKILLS[skill].slot) {
    return { gear: { ...l.gear, [slot]: piece.id }, from: now?.id ?? null, to: piece.id };
  }
  return { offer: piece.id };
};
